import { useEffect } from 'react';
import { escapeRegExp } from 'lodash';
import { useRecoilValue } from 'recoil';
import { userState } from 'state';
import {
  EnumUserType,
  useProcessorCustomerByIdQuery,
  type User,
  useSearchCustomersQuery,
  useVendorInHouseProducerOneQuery,
} from '../../../lib/graphql';
import { isProcessor as isProcessorHelper } from 'lib/userHelpers';

/**
 * Parenthetical marking the processor's own in-house producer in every picker
 * built on this hook. It replaces the usual person-name/email parenthetical,
 * which for an in-house identity degrades to a raw ObjectId: the record is
 * login-less by construction (no first/last name, no email), so the generic
 * fallback chain has nothing human left to show.
 */
const IN_HOUSE_OPTION_SUFFIX = 'Your farm';

/** The customer fields every option is built from. */
export interface CustomerLike {
  _id: string;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  phone?: string | null;
  company?: string | null;
}

/**
 * The customer-label rule for every picker in the app: company first with the
 * person (or their email, or — last resort — the record id) parenthesised
 * after it, and the bare person/email/id when there is no company.
 *
 * Canonical here rather than in any one picker: the cutsheet assignment-rule
 * search renders the same option list from the same resolver, and two copies
 * of the rule would drift.
 */
export function formatCustomerLabel(customer: CustomerLike): string {
  const name =
    `${customer.first_name ?? ''} ${customer.last_name ?? ''}`.trim();

  return customer.company
    ? `${customer.company} (${name || customer.email || customer._id})`
    : name || customer.email || customer._id;
}

export interface CustomerOption {
  _id: string;
  fullName: string;
  company?: string | null;
  email?: string | null;
  phone?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  /** True for the processor's own in-house producer identity. */
  isInHouse: boolean;
}

function toOption(customer: CustomerLike, isInHouse: boolean): CustomerOption {
  let label: string;
  if (isInHouse) {
    // Never fall through to the id for the in-house identity: it has no
    // person name and no email, so the generic chain would render its raw
    // ObjectId. Company (or a name, if a linked record happened to carry one)
    // plus the marker; the marker alone if it somehow has neither.
    const identity =
      customer.company ||
      `${customer.first_name ?? ''} ${customer.last_name ?? ''}`.trim();
    label = identity
      ? `${identity} (${IN_HOUSE_OPTION_SUFFIX})`
      : IN_HOUSE_OPTION_SUFFIX;
  } else {
    label = formatCustomerLabel(customer);
  }

  return {
    _id: customer._id,
    fullName: label,
    company: customer.company,
    email: customer.email,
    phone: customer.phone,
    first_name: customer.first_name,
    last_name: customer.last_name,
    isInHouse,
  };
}

/**
 * Whether a customer would have survived the resolver's own `search` filter.
 *
 * Mirrors `search-customers.ts` — a case-insensitive substring over
 * first/last name, email, company and phone — so the pinned selection is held
 * to exactly the test every other option in the list already passed. Both
 * sides escape the term, so the client never hides a record the server would
 * have matched.
 *
 * @param customer The record to test.
 * @param term Trimmed search term; an empty term matches everything.
 * @returns True when the record matches, or when there is no active search.
 */
function matchesSearch(customer: CustomerLike, term: string): boolean {
  if (!term) return true;

  const rx = new RegExp(escapeRegExp(term), 'i');
  return [
    customer.first_name,
    customer.last_name,
    customer.email,
    customer.company,
    customer.phone,
  ].some((field) => Boolean(field) && rx.test(field as string));
}

export interface UseCustomerOptionsConfig {
  includeAddNew?: boolean;
  relationshipType?: EnumUserType;
  producerId?: string;
  /**
   * Server-side search term. The resolver caps its result set, so a picker on a
   * vendor with more customers than the cap can only reach the rest by
   * narrowing here — client-side filtering of the capped page cannot. Callers
   * that pass this should debounce it and wire `InputTypeahead`'s
   * `onInputChange` so typing re-queries instead of filtering a stale page.
   */
  search?: string;
  /**
   * Id of an already-selected customer to keep in the options list. `search`
   * replaces the list on every keystroke, and a selected option missing from
   * `options` makes MUI's Autocomplete render an empty input over a value that
   * is still set — so the selection is fetched on its own and merged back in
   * whenever the current page doesn't contain it.
   *
   * The merge is gated on the selection matching the active `search`: while a
   * term is being typed nothing downstream filters this list (`InputTypeahead`
   * hands `filterOptions` straight through once `onInputChange` is wired), so
   * an unconditional merge would list the current selection above — and during
   * the debounce, instead of — the matches the user is typing to reach.
   */
  pinnedId?: string;
}

export function useCustomerOptions(
  vendorId: string | undefined,
  isOpen: boolean,
  options?: UseCustomerOptionsConfig,
) {
  const { relationshipType, producerId, search, pinnedId } = options ?? {};
  const currentUser = useRecoilValue(userState);
  const userIsProcessor = currentUser
    ? isProcessorHelper(currentUser as User)
    : false;
  const shouldLoad = Boolean(isOpen && vendorId);

  // Only include producerId filter when NOT a processor. Processors should see all their customers.
  const effectiveProducerId = userIsProcessor ? undefined : producerId;

  const variables = {
    vendorId: vendorId as string,
    ...(relationshipType ? { relationshipType } : {}),
    ...(effectiveProducerId ? { producerId: effectiveProducerId } : {}),
    ...(search?.trim() ? { search: search.trim() } : {}),
  } as {
    vendorId: string;
    relationshipType?: EnumUserType;
    producerId?: string;
    search?: string;
  };

  const { data, loading, refetch } = useSearchCustomersQuery({
    variables,
    skip: !shouldLoad,
    fetchPolicy: 'cache-and-network',
  });

  useEffect(() => {
    if (isOpen && vendorId) {
      refetch?.(variables);
    }
  }, [
    isOpen,
    vendorId,
    effectiveProducerId,
    relationshipType,
    refetch,
    userIsProcessor,
  ]);

  // The vendor's own in-house producer, so its option can be marked as such.
  // Session-derived server-side, so it only resolves for a processor session —
  // skipped otherwise, where it would return null anyway. Apollo caches the
  // result, so the several pickers on one screen share a single fetch.
  const { data: inHouseData } = useVendorInHouseProducerOneQuery({
    skip: !shouldLoad || !userIsProcessor,
  });
  const inHouseProducerId = inHouseData?.vendorInHouseProducerOne?.id;

  const isInHouse = (id: string) =>
    Boolean(inHouseProducerId) && id === String(inHouseProducerId);

  const searchResults = data?.searchCustomers ?? [];
  const trimmedSearch = search?.trim() ?? '';

  // Only fetch the pinned selection when the current page doesn't already
  // carry it, so the common case (no search, selection in the first page)
  // costs nothing extra. Fetched even while a non-matching search is active:
  // the record is what resolves the input's label the moment the term clears,
  // and it is one cache-first read of a record the picker was just showing.
  const pinnedIsMissing = Boolean(
    pinnedId && !searchResults.some((c) => String(c._id) === pinnedId),
  );
  const { data: pinnedData } = useProcessorCustomerByIdQuery({
    variables: { id: pinnedId as string },
    skip: !pinnedIsMissing,
    fetchPolicy: 'cache-first',
  });

  const customerOptions: CustomerOption[] = searchResults.map((customer) =>
    toOption(
      { ...customer, _id: String(customer._id) },
      isInHouse(String(customer._id)),
    ),
  );

  const pinnedCustomer = pinnedIsMissing ? pinnedData?.userById : undefined;
  if (
    pinnedCustomer &&
    matchesSearch(
      { ...pinnedCustomer, _id: String(pinnedCustomer._id) },
      trimmedSearch,
    )
  ) {
    customerOptions.unshift(
      toOption(
        { ...pinnedCustomer, _id: String(pinnedCustomer._id) },
        isInHouse(String(pinnedCustomer._id)),
      ),
    );
  }

  return { customerOptions, loading, refetchCustomers: refetch };
}
